import { Component, DestroyRef, Input, OnInit, inject } from '@angular/core';
import { isPlatformBrowser } from '@angular/common';
import { PLATFORM_ID } from '@angular/core';
import { MenuItem } from 'primeng/api';
import { Router, RouterModule } from '@angular/router';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { StyleClassModule } from 'primeng/styleclass';
import { TooltipModule } from 'primeng/tooltip';
import { LayoutService } from '../service/layout.service';
import { MenuModule } from 'primeng/menu';
import { SelectModule } from 'primeng/select';
import { AuthService } from '@app/core/services/auth.service';
import { NotificationBellComponent } from '@app/core/components/notification-bell/notification-bell.component';
import { AideBoutonComponent } from '@app/aide/composants/aide-bouton/aide-bouton.component';
import { PlanService } from '@app/core/services/plan.service';
import {
    DEFAULT_SHOP_ID,
    persistShopId,
    resolveShopIdForRequest,
} from '@app/core/storage/shop-id.storage';
import { AccessibleShopsStore } from '@app/core/stores/accessible-shops.store';
import { PageTitleService } from '@app/core/services/page-title.service';

@Component({
    selector: 'app-topbar',
    standalone: true,
    imports: [RouterModule, CommonModule, FormsModule, StyleClassModule, MenuModule, SelectModule, TooltipModule, NotificationBellComponent, AideBoutonComponent],
    template: `
    <div class="layout-topbar">
        <div class="layout-topbar-logo-container">
            @if (!hideMenuButton) {
                <button class="layout-menu-button layout-topbar-action" (click)="layoutService.onMenuToggle()">
                    <i class="fa-light fa-bars"></i>
                </button>
            }
            <div class="layout-topbar-module">
                <a class="layout-topbar-logo" routerLink="/">
                    <img src="assets/images/logo/logo-monicase-symbol.png" class="logo-icon" alt="MONI" />
                </a>
                @if (moduleLabel) {
                    <span class="layout-topbar-module-badge" [style.background]="moduleColor || 'var(--primary-color)'">
                        {{ moduleLabel }}
                    </span>
                }
            </div>
        </div>

        <div class="layout-topbar-actions">
            @if (showShopSwitcher) {
                <div class="layout-topbar-shop-select mr-2">
                    <p-select
                        [options]="shopSelectOptions"
                        optionLabel="label"
                        optionValue="id"
                        [(ngModel)]="selectedShopId"
                        (ngModelChange)="onShopChange($event)"
                        [style]="{ minWidth: '12rem' }"
                        styleClass="shop-select-noborder"
                        appendTo="body"
                        inputId="topbar-shop"
                    >
                        <ng-template #selectedItem let-opt>
                            <div class="shop-select-item">
                                @if (opt.image_url) {
                                    <img [src]="opt.image_url" class="shop-select-img" alt="" />
                                } @else {
                                    <span class="shop-select-fallback"><i class="fa-light fa-shop"></i></span>
                                }
                                <span>{{ opt.label }}</span>
                            </div>
                        </ng-template>
                        <ng-template #item let-opt>
                            <div class="shop-select-item">
                                @if (opt.image_url) {
                                    <img [src]="opt.image_url" class="shop-select-img" alt="" />
                                } @else {
                                    <span class="shop-select-fallback"><i class="fa-light fa-shop"></i></span>
                                }
                                <span>{{ opt.label }}</span>
                            </div>
                        </ng-template>
                    </p-select>
                </div>
            }

            <button class="layout-topbar-menu-button layout-topbar-action" pStyleClass="@next" enterFromClass="hidden" enterActiveClass="animate-scalein" leaveToClass="hidden" leaveActiveClass="animate-fadeout" [hideOnOutsideClick]="true">
                <i class="fa-light fa-ellipsis-v"></i>
            </button>

            <div class="layout-topbar-menu hidden lg:block">
                <div class="layout-topbar-menu-content">
                    <!-- Aide : panneau des tutos, qui propose la visite guidée de l'écran [D6]. -->
                    <app-aide-bouton />
                    <button type="button" class="layout-topbar-action" data-tour="app-drawer-btn"
                            pTooltip="Toutes les applications" tooltipPosition="bottom"
                            (click)="openLauncher($event)">
                        <i class="fa-light fa-th-large"></i>
                    </button>
                    <app-notification-bell />
                    <button type="button" class="layout-topbar-action" data-tour="profile-btn" (click)="menu.toggle($event)">
                        <i class="fa-light fa-user"></i>
                        <span>Profile</span>
                    </button>
                    <p-menu #menu [model]="profileItemsMenu" [popup]="true" />
                </div>
            </div>
        </div>
    </div>

    `,
    styles: [`
        /* ── Shop select ── */
        .shop-select-item {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        .shop-select-img {
            width: 1.5rem;
            height: 1.5rem;
            border-radius: 4px;
            /* contain et non cover : c'est un logo, il ne se recadre pas. */
            object-fit: contain;
            background: var(--surface-0);
            flex-shrink: 0;
        }
        .shop-select-fallback {
            width: 1.5rem;
            height: 1.5rem;
            border-radius: 4px;
            background: var(--surface-border);
            display: flex;
            align-items: center;
            justify-content: center;
            font-size: 0.75rem;
            flex-shrink: 0;
            color: var(--text-color);
        }

        .layout-topbar-module {
            display: flex;
            align-items: center;
            gap: 0.5rem;
        }
        .logo-icon {
            height: 2.2rem;
            width: auto;
            display: block;
        }
        .layout-topbar-module-badge {
            font-size: 0.72rem;
            font-weight: 800;
            letter-spacing: 0.08em;
            text-transform: uppercase;
            color: #fff;
            padding: 0.25rem 0.65rem;
            border-radius: 6px;
        }

    `]
})
export class AppTopbar implements OnInit {
    /** Masque le bouton d'ouverture du menu (ex. page wizard de bienvenue) */
    @Input() hideMenuButton = false;
    /** Nom du module courant affiché à côté du logo (ex. 'SKEDI') */
    @Input() moduleLabel: string | null = null;
    /** Couleur de fond du badge module */
    @Input() moduleColor: string | null = null;

    private platformId = inject(PLATFORM_ID);
    private isBrowser = isPlatformBrowser(this.platformId);
    private _planService = inject(PlanService);
    private _pageTitle = inject(PageTitleService);
    private _destroyRef = inject(DestroyRef);

    showShopSwitcher = false;
    shopSelectOptions: { id: number; label: string; image_url?: string | null }[] = [];
    selectedShopId = DEFAULT_SHOP_ID;

    profileItemsMenu: MenuItem[] = [];

    constructor(
        public layoutService: LayoutService,
        private _authService: AuthService,
        private _router: Router,
        private accessibleShopsStore: AccessibleShopsStore
    ) {}

    ngOnInit(): void {
        this.profileItemsMenu = [
            {
                label: 'Mon profil',
                icon: 'fa-light fa-user',
                command: () => { this._router.navigate(['moni/profile']); }
            },
            ...(this._planService.isSolo() ? [] : [{
                label: 'Changer d\'application',
                icon: 'fa-light fa-arrow-right-arrow-left',
                command: () => { this.openLauncher(); }
            }]),
            {
                label: 'Déconnexion',
                icon: 'fa-light fa-sign-out',
                command: () => { this._authService.logout(); }
            },
        ];

        this.selectedShopId = resolveShopIdForRequest(this.isBrowser);

        // Titre de l'onglet : « Boutique - Page » (cf. PageTitleService).
        if (this.isBrowser) {
            this._pageTitle.setModuleLabel(this.moduleLabel);
            this._pageTitle.start(this._destroyRef);
        }

        // [Revue finale, tour 2 — Finding 2] Le cadrage des boutiques accessibles vit
        // désormais dans `AccessibleShopsStore` : cette barre en était le seul détenteur,
        // alors que tout écran qui CHOISIT une boutique à la place de l'utilisateur doit
        // choisir dans la même liste. Les deux branches de `showShopSwitcher` étaient déjà
        // identiques (`filteredShops.length > 1`), le drapeau `accessibleIds === null` n'y
        // servait donc à rien et n'a pas eu à être exposé.
        this.accessibleShopsStore.accessibleShops$.subscribe((filteredShops) => {
            this.showShopSwitcher = filteredShops.length > 1;
            this._pageTitle.setShopName(AppTopbar.nomBoutique(filteredShops, this.selectedShopId));

            if (!this.showShopSwitcher) return;

            this.shopSelectOptions = [
                { id: -1, label: 'Tous les magasins', image_url: null },
                ...filteredShops.map((s: any) => ({
                    id: Number(s.id),
                    label: (s.name as string) ?? (s.name?.fr as string) ?? `Magasin ${s.id}`,
                    image_url: AppTopbar.vignetteDe(s),
                })),
            ];

            const allowed = new Set(this.shopSelectOptions.map((o) => o.id));
            if (!allowed.has(this.selectedShopId)) {
                const firstId = this.shopSelectOptions.find((o) => o.id !== -1)?.id ?? DEFAULT_SHOP_ID;
                this.selectedShopId = firstId;
                persistShopId(this.isBrowser, this.selectedShopId);
                this._pageTitle.setShopName(AppTopbar.nomBoutique(filteredShops, this.selectedShopId));
            }
        });
    }

    /** Nom de la boutique en cours pour le titre de l'onglet (« Tous les magasins » si -1). */
    private static nomBoutique(shops: any[], selectedId: number): string | null {
        if (selectedId === -1 && shops.length > 1) return 'Tous les magasins';
        const shop = shops.find((s) => Number(s.id) === Number(selectedId)) ?? (shops.length === 1 ? shops[0] : null);
        const name = shop?.name;
        return typeof name === 'string' ? name : (name?.fr ?? null);
    }

    /**
     * Vignette d'une boutique dans le sélecteur : le LOGO d'abord.
     *
     * Le sélecteur affichait `image_url`, qui vaut `/shops/default.png` par
     * défaut pour toutes les boutiques : chaque entrée montrait donc la même
     * image, ce qui ne distingue rien — et le fichier est absent sur la plupart
     * des instances, produisant une image cassée. On écarte cette valeur par
     * défaut et on retombe sur l'icône quand il n'y a pas de logo.
     */
    private static vignetteDe(shop: any): string | null {
        if (shop?.logo_url) return shop.logo_url;
        const image = shop?.image_url;
        if (!image || typeof image !== 'string' || image.endsWith('/shops/default.png')) return null;
        return image;
    }

    openLauncher(event?: MouseEvent): void {
        if (event?.ctrlKey || event?.metaKey) {
            window.open('/launcher', '_blank');
        } else {
            this._router.navigate(['/launcher']);
        }
    }

    onShopChange(shopId: number): void {
        if (shopId === undefined || shopId === null || !Number.isFinite(Number(shopId))) return;
        persistShopId(this.isBrowser, Number(shopId));
        if (this.isBrowser) {
            sessionStorage.setItem('moni.pendingShopChange', String(shopId));
            globalThis.location.reload();
        }
    }

    toggleDarkMode() {
        this.layoutService.layoutConfig.update((state) => ({ ...state, darkTheme: !state.darkTheme }));
    }
}
